// A synthesized ringtone (no audio asset needed) via WebAudio: a two-tone chirp
// repeated on a loop. play() starts the loop, stop() ends it. Safe if WebAudio is
// unavailable (returns no-ops).
import { useEffect, useRef } from "react";

export function useRingtone() {
  const ctxRef = useRef<AudioContext | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const ensureCtx = (): AudioContext | null => {
    if (ctxRef.current) return ctxRef.current;
    const Ctx = (window as any).AudioContext || (window as any).webkitAudioContext;
    if (!Ctx) return null;
    ctxRef.current = new Ctx();
    return ctxRef.current;
  };

  const chirp = () => {
    const ctx = ensureCtx();
    if (!ctx) return;
    if (ctx.state === "suspended") ctx.resume().catch(() => {});
    const now = ctx.currentTime;
    // Classic two-tone ring (~440/480 Hz), short and soft.
    [440, 480].forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      const t0 = now + i * 0.4;
      gain.gain.setValueAtTime(0.0001, t0);
      gain.gain.exponentialRampToValueAtTime(0.18, t0 + 0.05);
      gain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.35);
      osc.connect(gain).connect(ctx.destination);
      osc.start(t0);
      osc.stop(t0 + 0.4);
    });
  };

  const play = () => {
    if (timerRef.current) return;
    chirp();
    timerRef.current = setInterval(chirp, 2500);
  };

  const stop = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  useEffect(() => () => stop(), []);
  return { play, stop };
}
